// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useEffect, useRef, useState } from 'react';
import {
  Modal,
  Button,
  Dropdown,
  FigmaUploadFilledIcon,
} from '@loa/ui-components';

/**
 * Document Type options for the advisor relationship upload. Value = the
 * {@code RelationshipDocumentType} enum name on the server; text = the label
 * rendered in the dropdown. Keep in sync with
 * {@code com.loa.enums.RelationshipDocumentType}.
 */
export const RELATIONSHIP_DOCUMENT_TYPE_OPTIONS: ReadonlyArray<{ value: string; text: string }> = [
  { value: 'LOA_REQUEST', text: 'LoA Request' },
  { value: 'COA_REQUEST', text: 'CoA Request' },
  { value: 'LEAVING_SERVICE_OPTIONS', text: 'Leaving Service Options' },
  { value: 'RETIREMENT_OPTIONS', text: 'Retirement Options' },
  { value: 'FUND_SWITCH_REQUEST', text: 'Fund Switch Request' },
  { value: 'PENSION_TRANSFER_REQUEST', text: 'Pension Transfer Request' },
  { value: 'INVESTMENT_WITHDRAWAL', text: 'Investment Withdrawal' },
  { value: 'POLICY_CANCELLATION_REQUEST', text: 'Policy Cancellation Request' },
  { value: 'APPLICATION_FORM', text: 'Application form' },
  { value: 'STATEMENT_OF_SUITABILITY', text: 'Statement of Suitability' },
  { value: 'UPLOAD', text: 'Upload' },
  { value: 'AML', text: 'AML' },
];

interface UploadRelationshipDocumentModalProps {
  open: boolean;
  onClose: () => void;
  onUpload?: (documentName: string, documentType: string, file: File) => Promise<void> | void;
}

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/jpeg',
  'image/png',
].join(',');

const labelClass =
  "font-sans text-body-03 font-medium text-black mb-sm ";

const inputClass =
  "h-12 px-4 border border-grey-04 rounded-lg bg-white font-sans text-base text-black outline-none box-border focus:border-purple-primary disabled:bg-grey-06 disabled:cursor-not-allowed";

const dropzoneBase =
  'flex flex-col items-center justify-center gap-2 p-6 bg-grey-06 rounded-2xl border border-dashed transition-colors duration-150';

/**
 * Advisor "Upload Document" modal for the relationship Documents tab. Mirrors the
 * client {@code UploadDocumentModal} (same Document Name field, dropzone and
 * actions) but drops the policy-linking question — these uploads are always
 * relationship-scoped — and adds a required "Document Type" classification.
 */
export function UploadRelationshipDocumentModal({
  open,
  onClose,
  onUpload,
}: Readonly<UploadRelationshipDocumentModalProps>) {
  const [documentName, setDocumentName] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sizeError, setSizeError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setDocumentName('');
      setDocumentType('');
      setSelectedFile(null);
      setIsDragging(false);
      setUploading(false);
      setSizeError(null);
    }
  }, [open]);

  const applySelectedFile = (file: File) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setSelectedFile(null);
      setSizeError('File is larger than 50MB. Please choose a smaller file.');
      return;
    }
    setSizeError(null);
    setSelectedFile(file);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) applySelectedFile(file);
    event.target.value = '';
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) applySelectedFile(file);
  };

  const handleUploadClick = async () => {
    if (!documentName.trim() || !documentType || !selectedFile || !onUpload) {
      onClose();
      return;
    }
    setUploading(true);
    try {
      await onUpload(documentName.trim(), documentType, selectedFile);
      onClose();
    } finally {
      setUploading(false);
    }
  };

  const canUpload =
    documentName.trim().length > 0 &&
    documentType !== '' &&
    selectedFile !== null &&
    !uploading;

  return (
    <Modal open={open} onClose={onClose} size="large" className="fin-modal--compact">
      <Modal.Header icon="upload filled" title="Upload Document" onClose={onClose} />
      <Modal.Content>
        <div className="flex flex-col">
          <label htmlFor="upload-document-name" className={labelClass}>
            Document Name<span className="text-[#e7000b] ml-0.5">*</span>
          </label>
          <input
            id="upload-document-name"
            type="text"
            className={inputClass}
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
            disabled={uploading}
          />
        </div>

        <div className="mt-lg flex flex-col">
          <label className={labelClass}>
            Document Type<span className="text-[#e7000b] ml-0.5">*</span>
          </label>
          <Dropdown
            placeholder="Select document type"
            fluid
            options={[...RELATIONSHIP_DOCUMENT_TYPE_OPTIONS]}
            value={documentType}
            onChange={setDocumentType}
            disabled={uploading}
            data-testid="upload-document-type-select"
          />
        </div>

        <div className="mt-lg flex flex-col">
          <label className={labelClass}>Upload Documents</label>
          <div
            className={`${dropzoneBase} ${
              isDragging ? 'border-purple-primary bg-[#eeeaf6]' : 'border-transparent'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <FigmaUploadFilledIcon size={24} />
            <p className="m-0 font-sans text-body-03 font-normal leading-normal text-black">
              {selectedFile ? selectedFile.name : 'Drag & drop here or'}
            </p>
            <Button
              variant="outlined"
              colour="purple"
              size="sm"
              onClick={handleBrowseClick}
              disabled={uploading}
            >
              Browse Files
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>
          <p className="mt-2 m-0 font-sans text-body-04 text-[#767676]">
            Accepted file type: PDF, Word, Excel, JPG, PNG (max 50MB)
          </p>
          {sizeError && (
            <p className="mt-sm m-0 font-sans text-body-04 text-[#e7000b]">
              {sizeError}
            </p>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{
          label: 'Cancel',
          onClick: onClose,
          disabled: uploading,
        }}
        primaryAction={{
          label: 'Upload',
          onClick: handleUploadClick,
          disabled: !canUpload,
          loading: uploading,
        }}
      />
    </Modal>
  );
}
